import { EntityAdapter, createEntityAdapter, EntityState } from '@ngrx/entity';
import {
  Reason,
  PresenceType,
  AppointmentState,
  Version,
  MhDepartment,
  MhStock,
  Firm,
  Initialization,
  PersonConfigDto,
  HexServingMode,
  HexAdministration,
  AnimalType,
  SpeciesDiagnosesMappedDto,
  TimeRangeConfig,
  IsoCountry,
  Location,
  BookSetting,
  Translation,
  CustomerExternalNumber,
  AnimalExternalNumber,
  AnimalDateType,
  CustomerCategoryGroup,
  CustomerCategory
} from 'src/app/api/models';
import { StaticData } from 'src/app/calendar/shared/models/static-data.model';
import { RgTextList } from 'src/app/medical-history/models/rg-text-list.model';

export const adapter: EntityAdapter<StaticData> = createEntityAdapter<StaticData>();

export interface State extends EntityState<StaticData> {
  reasons: Reason[],
  presenceTypes: PresenceType[],
  appointmentStates: AppointmentState[],
  departments: MhDepartment[],
  firms: Firm[],
  locations: Location[],
  inventoryList: MhStock[],
  mhInitialization: Initialization | undefined,
  isloadedMedicalHistory: boolean,
  isLoadedHexData: boolean,
  isLoadedServiceArticleTreatment: boolean,
  version: Version,
  serviceArticleTreatments: RgTextList[],
  userConfig: PersonConfigDto,
  animalTypes: Array<AnimalType>,
  isLoadedAnimalTypes: boolean
  speciesDiagnoses: SpeciesDiagnosesMappedDto | undefined,
  isLoadedSpeciesDiagnosis: boolean,
  applicationTypes: HexAdministration[],
  servingModes: HexServingMode[] | undefined,
  timeRangeConfigs: TimeRangeConfig[],
  countryList: IsoCountry[],
  isCountryListLoaded: boolean,
  countyList: string[],
  isCountyListLoaded: boolean,
  bookSettingList: Array<BookSetting>,
  isBookSettingListLoaded: boolean,
  translations: Translation[],
  isCustomerExternalNumbersLoaded: boolean,
  isAnimalExternalNumbersLoaded: boolean,
  isAnimalDateTypesLoaded: boolean,
  customerExternalNumbers: Array<CustomerExternalNumber>,
  animalExternalNumbers: Array<AnimalExternalNumber>,
  animalDateTypes: Array<AnimalDateType>,
  customerCategoryGroup: Array<CustomerCategoryGroup>,
  isCustomerCategoryGroupLoaded: boolean,
  isCustomerCategoryLoaded: boolean,
  customerCategory: Array<CustomerCategory>,
}

export const initialState: State = adapter.getInitialState({
  reasons: [],
  presenceTypes: [],
  appointmentStates: [],
  timeRangeConfigs: [],
  departments: [],
  isloadedMedicalHistory: false,
  isLoadedHexData: false,
  isLoadedServiceArticleTreatment: false,
  firms: [],
  locations: [],
  inventoryList: [],
  mhInitialization: undefined,
  applicationTypes: [],
  servingModes: [],
  version: {
    commitHash: '',
    commitTime: '',
    tagCommitCount: 0,
    tagName: ''
  },
  serviceArticleTreatments: [],
  userConfig: {
    isNotificationEnabled: false
  },
  animalTypes: [],
  isLoadedAnimalTypes: false,
  speciesDiagnoses: undefined,
  isLoadedSpeciesDiagnosis: false,
  countryList: [],
  isCountryListLoaded: false,
  countyList: [],
  isCountyListLoaded: false,
  bookSettingList: [],
  isBookSettingListLoaded: false,
  translations: [],
  isCustomerExternalNumbersLoaded: false,
  isAnimalExternalNumbersLoaded: false,
  isAnimalDateTypesLoaded: false,
  customerExternalNumbers: [],
  animalExternalNumbers: [],
  animalDateTypes: [],
  customerCategoryGroup: [],
  isCustomerCategoryGroupLoaded: false,
  isCustomerCategoryLoaded: false,
  customerCategory: []
});

export const getReasons = (state: State) => state.reasons;
export const getPresenceTypes = (state: State) => state.presenceTypes;
export const getAppointmentStates = (state: State) => state.appointmentStates;
export const getUserConfig = (state: State) => state.userConfig;
export const getTimeRangeConfigs = (state: State) => state.timeRangeConfigs;
export const getVersion = (state: State) => state.version;
export const getDepartments = (state: State) => state.departments;
export const getFirms = (state: State) => state.firms;
export const getInventoryList = (state: State) => state.inventoryList;
export const getLocations = (state: State) => state.locations;
export const getMhInitialization = (state: State) => state.mhInitialization;
export const getIsLoadMedicalHistory = (state: State) => state.isloadedMedicalHistory;
export const getIsLoadedServiceArticleTreatment = (state: State) => state.isLoadedServiceArticleTreatment;
export const getServiceArticleTreatments = (state: State) => state.serviceArticleTreatments
export const getIsNotificationEnabled = (state: State) => state.userConfig.isNotificationEnabled;
export const getAnimalTypes = (state: State) => state.animalTypes;
export const speciesDiagnoses = (state: State) => state.speciesDiagnoses;
export const getHexSpecies = (state: State) => state.speciesDiagnoses?.species;
export const getIsLoadedSpecisDiagnosis = (state: State) => state.isLoadedSpeciesDiagnosis;
export const getCountryList = (state: State) => state.countryList;
export const getIsCountryListLoaded = (state: State) => state.isCountryListLoaded;
export const getCountyList = (state: State) => state.countyList;
export const getIsCountyListLoaded = (state: State) => state.isCountyListLoaded;
export const getBookSettingList = (state: State) => state.bookSettingList;
export const getIsBookSettingListLoaded = (state: State) => state.isBookSettingListLoaded;
export const getTranslations = (state: State) => state.translations;
export const getCustomerExternalNumbers = (state: State) => state.customerExternalNumbers;
export const getAnimalExternalNumbers = (state: State) => state.animalExternalNumbers;
export const getAnimalDateTypes = (state: State) => state.animalDateTypes;
export const getCustomerCategoryGroup = (state: State) => state.customerCategoryGroup;
export const getCustomerCategory = (state: State) => state.customerCategory;

